import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  isAndroidMobile,
  isIosMobile,
  isPifAppInstalled,
  isPifAppInstalledSync,
  openContinueApp,
  markPifAppInstalled,
  noteAppStoreVisit,
  watchForPifAppInstall,
  subscribePifInstall,
} from '../../../../utils/seo';
import './AppExclusiveDeals.css';

const qrUrl = (data) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(data)}`;

const BASE_SLIDES = [
  {
    id: 'android',
    store: 'Google Play',
    platform: 'Android',
    url: PLAY_STORE_URL,
    qr: qrUrl(PLAY_STORE_URL),
  },
  {
    id: 'ios',
    store: 'App Store',
    platform: 'iOS',
    url: APP_STORE_URL,
    qr: qrUrl(APP_STORE_URL),
  },
];

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="currentColor" d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.6 3 21.09 3 20.5ZM16.81 15.12L6.05 21.34L14.54 12.85L16.81 15.12ZM20.16 10.81C20.5 11.08 20.75 11.5 20.75 12C20.75 12.5 20.53 12.9 20.18 13.18L17.89 14.5L15.39 12L17.89 9.5L20.16 10.81ZM6.05 2.66L16.81 8.88L14.54 11.15L6.05 2.66Z" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="currentColor" d="M16.7 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9s-1.8-1-3-1c-1.6 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.3 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-.1 2.9-2.3c1.1-1.5 1.5-2.9 1.5-3 0 0-2.8-1.1-2.8-4.2zM14.8 6.2c.6-.8 1.1-1.9.9-3-1 .1-2.1.7-2.8 1.5-.6.7-1.2 1.8-1 2.9 1.1.1 2.2-.6 2.9-1.4z" />
  </svg>
);

const AppExclusiveDeals = () => {
  // Only "Continue" when we know the native app is installed.
  const [continueMode, setContinueMode] = useState(() => isPifAppInstalledSync());

  useEffect(() => {
    let cancelled = false;
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;

    const applyKnown = (known) => {
      if (cancelled) return;
      setContinueMode(!!known || isPifAppInstalledSync());
      if (known) markPifAppInstalled();
    };

    const run = async () => {
      try {
        const known = await isPifAppInstalled();
        applyKnown(known);
        if (!known && isAndroidMobile()) {
          watchForPifAppInstall({ timeoutMs: 25000, intervalMs: 2000, signal: ac?.signal })
            .then((found) => {
              if (found) applyKnown(true);
            })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) setContinueMode(false);
      }
    };
    run();

    const unsubscribe = subscribePifInstall((known) => {
      if (!cancelled) setContinueMode(!!known || isPifAppInstalledSync());
    });

    return () => {
      cancelled = true;
      try { ac?.abort(); } catch { /* ignore */ }
      unsubscribe();
    };
  }, []);

  const onGetStore = (event, kind) => {
    const native = (kind === 'ios' && isIosMobile()) || (kind === 'android' && isAndroidMobile());
    if (!native) return;
    event.preventDefault();
    openContinueApp({ knownInstalled: false, storeFallback: true });
  };

  const onContinue = (event) => {
    event.preventDefault();
    openContinueApp({ knownInstalled: true, storeFallback: false });
  };

  const slides = useMemo(() => {
    try {
      if (isIosMobile()) return [BASE_SLIDES[1], BASE_SLIDES[0]];
      if (isAndroidMobile()) return [BASE_SLIDES[0], BASE_SLIDES[1]];
    } catch {
      /* ignore */
    }
    return BASE_SLIDES;
  }, []);

  const [index, setIndex] = useState(0);
  const [dragPx, setDragPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [slideWidth, setSlideWidth] = useState(248);

  const viewportRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const lastDrag = useRef(0);
  const indexRef = useRef(0);
  const activePointer = useRef(null);
  const lockAxis = useRef(null); // 'x' | 'y' | null

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setSlideWidth(w);
    };

    measure();
    requestAnimationFrame(measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  const goTo = useCallback((next) => {
    const max = slides.length - 1;
    const clamped = Math.max(0, Math.min(max, next));
    indexRef.current = clamped;
    setIndex(clamped);
    setDragPx(0);
    lastDrag.current = 0;
    setIsDragging(false);
    lockAxis.current = null;
  }, [slides.length]);

  const beginDrag = (clientX, clientY, pointerId) => {
    activePointer.current = pointerId;
    startX.current = clientX;
    startY.current = clientY;
    lastDrag.current = 0;
    lockAxis.current = null;
    setIsDragging(true);
  };

  const moveDrag = (clientX, clientY) => {
    const dx = clientX - startX.current;
    const dy = clientY - startY.current;

    if (!lockAxis.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return false;
      lockAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (lockAxis.current !== 'x') return false;

    const atStart = indexRef.current === 0 && dx > 0;
    const atEnd = indexRef.current === slides.length - 1 && dx < 0;
    const next = atStart || atEnd ? dx * 0.28 : dx;
    lastDrag.current = next;
    setDragPx(next);
    return true;
  };

  const endDrag = () => {
    if (activePointer.current == null) return;
    activePointer.current = null;
    const delta = lastDrag.current;
    const threshold = Math.max(36, slideWidth * 0.18);
    const current = indexRef.current;

    if (lockAxis.current === 'x' && delta <= -threshold) goTo(current + 1);
    else if (lockAxis.current === 'x' && delta >= threshold) goTo(current - 1);
    else {
      setDragPx(0);
      lastDrag.current = 0;
      setIsDragging(false);
      lockAxis.current = null;
    }
  };

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    beginDrag(e.clientX, e.clientY, e.pointerId);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (activePointer.current !== e.pointerId) return;
    const handled = moveDrag(e.clientX, e.clientY);
    if (handled) e.preventDefault();
  };

  const finishPointer = (e) => {
    if (activePointer.current !== e.pointerId) return;
    endDrag();
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* already released */
    }
  };

  // Native touch fallback for older iOS / WebViews where pointer events are flaky.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (!e.touches?.[0]) return;
      const t = e.touches[0];
      beginDrag(t.clientX, t.clientY, 'touch');
    };
    const onTouchMove = (e) => {
      if (activePointer.current !== 'touch' || !e.touches?.[0]) return;
      const t = e.touches[0];
      const handled = moveDrag(t.clientX, t.clientY);
      if (handled) e.preventDefault();
    };
    const onTouchEnd = () => {
      if (activePointer.current !== 'touch') return;
      endDrag();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [slideWidth, slides.length]);

  const translateX = -(index * slideWidth) + dragPx;

  return (
    <section className="app-deals" aria-labelledby="app-deals-title">
      <div className="app-deals__inner">
        <div className="app-deals__content">
          <span className="app-deals__badge">
            {continueMode ? 'App Installed' : 'Mobile Exclusive'}
          </span>
          <h2 id="app-deals-title" className="app-deals__title">
            {continueMode ? 'Continue App' : 'Get the PIF App'}
          </h2>
          <p className="app-deals__desc">
            {continueMode
              ? 'Open the Properties Infinite app for early access to premium listings, instant alerts, and member-only offers.'
              : "Download the Properties Infinite app for early access to premium listings, instant alerts, and member-only offers you won't find on the web."}
          </p>
          <ul className="app-deals__perks">
            <li>App-only pricing on select properties</li>
            <li>Real-time push notifications</li>
            <li>Faster booking &amp; enquiry flow</li>
          </ul>
          <div className="app-deals__actions">
            {continueMode ? (
              <>
                <button
                  type="button"
                  className="app-deals__play-btn app-deals__play-btn--continue"
                  onClick={onContinue}
                >
                  <span>Continue App</span>
                </button>
                <a
                  href={isIosMobile() ? APP_STORE_URL : PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-deals__play-btn app-deals__play-btn--ghost"
                  onClick={() => noteAppStoreVisit()}
                >
                  <span>{isIosMobile() ? 'App Store' : 'Google Play'}</span>
                </a>
              </>
            ) : (
              <>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-deals__play-btn"
                  onClick={(event) => onGetStore(event, 'android')}
                >
                  <PlayIcon />
                  <span>Get it on Google Play</span>
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-deals__play-btn app-deals__play-btn--ios"
                  onClick={(event) => onGetStore(event, 'ios')}
                >
                  <AppleIcon />
                  <span>Download on the App Store</span>
                </a>
              </>
            )}
          </div>
        </div>

        <div className="app-deals__qr-wrap">
          <div
            className={`app-deals__qr-viewport${isDragging ? ' is-dragging' : ''}`}
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finishPointer}
            onPointerCancel={finishPointer}
            role="region"
            aria-roledescription="carousel"
            aria-label="App store QR codes"
          >
            <div
              className={`app-deals__qr-track${isDragging ? ' is-dragging' : ''}`}
              style={{ transform: `translate3d(${translateX}px, 0, 0)` }}
            >
              {slides.map((slide, i) => (
                <div
                  key={slide.id}
                  className="app-deals__qr-slide"
                  style={{ flex: `0 0 ${slideWidth}px`, width: slideWidth }}
                  aria-hidden={i !== index}
                >
                  <div className="app-deals__qr-card">
                    <img
                      src={slide.qr}
                      alt={`QR code to download Properties Infinite on ${slide.store}`}
                      className="app-deals__qr"
                      draggable="false"
                    />
                    <p className="app-deals__qr-label">Scan to download</p>
                    <span className="app-deals__qr-app">{slide.platform}</span>
                    <span className="app-deals__qr-store">{slide.store}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="app-deals__qr-nav" role="tablist" aria-label="App store QR codes">
            <button
              type="button"
              className="app-deals__qr-arrow"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              aria-label="Previous QR code"
            >
              ‹
            </button>
            <div className="app-deals__qr-dots">
              {slides.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  className={`app-deals__qr-dot${i === index ? ' is-active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`${slide.platform} QR code`}
                />
              ))}
            </div>
            <button
              type="button"
              className="app-deals__qr-arrow"
              onClick={() => goTo(index + 1)}
              disabled={index === slides.length - 1}
              aria-label="Next QR code"
            >
              ›
            </button>
          </div>
          <p className="app-deals__swipe-hint">Swipe for Android / iOS</p>
        </div>
      </div>
    </section>
  );
};

export default AppExclusiveDeals;
